import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Anchor, Target, Users, Zap, ShieldCheck, Mail, Github, Twitter, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { YachtLogo } from '../components/YachtLogo';

import Navbar from '../components/Navbar';

const About = () => {
  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-zinc-100 selection:text-black flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-48 pb-32 px-8 border-b border-white/5">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-6xl md:text-8xl font-mono uppercase tracking-tighter leading-[0.9] mb-8">
                Synthesizing the <br/>
                <span className="text-[#0074D9]">Future of Software.</span>
              </h1>
              <p className="text-zinc-400 text-lg md:text-xl font-light max-w-2xl leading-relaxed">
                Yacht Labs is a modern AI research and product studio dedicated to shortening the loop from 'what if' to 'it's live'.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Company History Section */}
        <section className="py-32 px-8 bg-zinc-950 border-b border-white/5">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-24 items-start">
            <div>
              <h2 className="tech-label mb-8">Our Mission</h2>
              <h3 className="text-4xl font-mono uppercase tracking-tighter mb-8 leading-tight">
                Bridging the Gap Between <br/>
                No-Code and Code.
              </h3>
              <div className="space-y-6 text-zinc-400 font-light leading-relaxed text-lg italic">
                <p>
                  Most builders today are stuck between two extremes. On one side, CMS tools like Squarespace and Webflow offer speed but limit your ceiling. On the other, full-stack environments like Replit or Lovable offer power but demand high technical friction.
                </p>
                <p>
                  Yacht Labs exists in the middle. We are building the next generation of software creation by combining the intuitive speed of a CMS with the unrestricted flexibility of a high-performance app builder.
                </p>
                <p>
                  We believe that in the age of AI, the bottleneck shouldn't be your ability to write code—it should be your ability to refine intent. Our synthesis engine turns raw vision into production-ready architecture in seconds.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-8 bg-black border border-white/5 rounded-3xl space-y-4">
                <Zap className="text-[#0074D9]" size={24} />
                <h4 className="text-sm font-mono uppercase tracking-widest text-white font-bold">Velocity</h4>
                <p className="text-xs text-zinc-500 font-light leading-relaxed">Idea to live build in under 300 seconds. No setup, no friction.</p>
              </div>
              <div className="p-8 bg-black border border-white/5 rounded-3xl space-y-4">
                <ShieldCheck className="text-[#0074D9]" size={24} />
                <h4 className="text-sm font-mono uppercase tracking-widest text-white font-bold">Integrity</h4>
                <p className="text-xs text-zinc-500 font-light leading-relaxed">Clean, production-grade architecture. No hidden slop or vendor lock.</p>
              </div>
              <div className="p-8 bg-black border border-white/5 rounded-3xl space-y-4">
                <Users className="text-[#0074D9]" size={24} />
                <h4 className="text-sm font-mono uppercase tracking-widest text-white font-bold">Ownership</h4>
                <p className="text-xs text-zinc-500 font-light leading-relaxed">Your data. Your source. Your vision. We just assemble the parts.</p>
              </div>
              <div className="p-8 bg-black border border-white/5 rounded-3xl space-y-4">
                <Target className="text-[#0074D9]" size={24} />
                <h4 className="text-sm font-mono uppercase tracking-widest text-white font-bold">Precision</h4>
                <p className="text-xs text-zinc-500 font-light leading-relaxed">Fine-tuned synthesis for high-conversion assets and interactive apps.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Founder Section */}
        <section className="py-32 px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="tech-label mb-16 text-center">From the Bridge</h2>
            
            <div className="relative flex flex-col md:flex-row items-center gap-16 p-12 bg-zinc-900/30 border border-zinc-800 rounded-[48px] overflow-hidden">
              <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
                <Anchor size={240} />
              </div>
              
              <div className="w-64 h-64 md:w-80 md:h-80 shrink-0 rounded-3xl overflow-hidden border-2 border-[#0074D9] shadow-[0_0_50px_rgba(0,116,217,0.2)] transition-all duration-700 bg-zinc-900">
                <img 
                  src="/headshot.jpg" 
                  alt="Cheryl Qian" 
                  className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all"
                  onError={(e) => {
                    e.currentTarget.src = "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=800&h=800";
                  }}
                  referrerPolicy="no-referrer"
                />
              </div>

              <div className="flex-1 space-y-8">
                <div>
                  <h3 className="text-4xl font-mono uppercase tracking-tighter text-white mb-2">Cheryl Qian</h3>
                  <p className="text-[#0074D9] font-mono text-sm uppercase tracking-[0.2em]">Founder & CEO</p>
                </div>

                <div className="space-y-6 text-zinc-400 font-light text-lg leading-relaxed">
                  <p>
                    Cheryl is a product designer, engineer, and AI strategist focused on the intersection of creative intent and technical execution. 
                  </p>
                  <p>
                    Before founding Yacht Labs, she spent years building cross-functional products that leveraged emerging tech to solve complex architectural problems. Her background in both high-end design systems and production-grade engineering allows her to bridge the translation gap that often stalls ambitious projects.
                  </p>
                  <p>
                    At Yacht Labs, her mission is to democratize the 'dangerous' developer capability—giving non-technical founders and smaller teams the power to ship high-quality, editable, and scalable software without the traditional overhead.
                  </p>
                </div>

                <div className="flex gap-6 pt-4">
                  <a href="#" className="p-3 bg-zinc-800/50 border border-zinc-700 rounded-xl text-zinc-400 hover:text-white hover:border-zinc-500 transition-all">
                    <Twitter size={20} />
                  </a>
                  <a href="#" className="p-3 bg-zinc-800/50 border border-zinc-700 rounded-xl text-zinc-400 hover:text-white hover:border-zinc-500 transition-all">
                    <Github size={20} />
                  </a>
                  <a href="#" className="p-3 bg-zinc-800/50 border border-zinc-700 rounded-xl text-zinc-400 hover:text-white hover:border-zinc-500 transition-all">
                    <Linkedin size={20} />
                  </a>
                  <a href="mailto:cheryl@yacht-labs.com" className="p-3 bg-zinc-800/50 border border-zinc-700 rounded-xl text-zinc-400 hover:text-white hover:border-zinc-500 transition-all">
                    <Mail size={20} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Join CTA */}
        <section className="py-32 px-8 bg-zinc-950 border-y border-white/5">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-xs font-mono uppercase tracking-[0.4em] text-[#0074D9]">Ready to Start?</h2>
            <h3 className="text-5xl font-mono uppercase tracking-tighter text-white">The Lab is Open.</h3>
            <p className="text-zinc-500 text-lg max-w-xl mx-auto font-light">
              Join the first batch of founders synthesizing the future. Claim your berth today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-8">
              <Link to="/#auth" className="px-12 py-5 bg-white text-black font-mono uppercase tracking-widest text-sm font-bold rounded-2xl hover:bg-zinc-200 transition-all hover:scale-[1.02] flex items-center gap-3">
                Secure Entry <ArrowRight size={18} />
              </Link>
              <Link to="/gallery" className="px-12 py-5 border border-zinc-800 text-zinc-400 font-mono uppercase tracking-widest text-sm rounded-2xl hover:bg-zinc-900 transition-all">
                View Gallery
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="p-12 border-t border-zinc-900 bg-black">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-start gap-12">
          <div className="space-y-6 max-w-xs">
            <Link to="/" className="flex items-center gap-3">
              <YachtLogo className="w-6 h-6" />
              <span className="font-mono text-lg uppercase tracking-widest">Yacht Labs</span>
            </Link>
            <p className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest leading-relaxed">
              Synthesizing the future of digital architecture. Build fast. Ship faster.
            </p>
            <div className="flex gap-4">
              <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="p-2 border border-zinc-900 rounded-lg text-zinc-500 hover:text-[#0074D9] hover:border-[#0074D9]/50 transition-all">
                <Github size={16} />
              </a>
              <a href="mailto:hello@yacht-labs.com" className="p-2 border border-zinc-900 rounded-lg text-zinc-500 hover:text-[#0074D9] hover:border-[#0074D9]/50 transition-all">
                <Mail size={16} />
              </a>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-12">
            <div className="space-y-4">
              <h5 className="text-[10px] font-mono uppercase tracking-widest text-white">Product</h5>
              <div className="flex flex-col gap-2">
                <Link to="/product" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Platform</Link>
                <Link to="/use-cases" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Use Cases</Link>
                <Link to="/solutions" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Solutions</Link>
                <Link to="/pricing" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Pricing</Link>
              </div>
            </div>
            <div className="space-y-4">
              <h5 className="text-[10px] font-mono uppercase tracking-widest text-white">Company</h5>
              <div className="flex flex-col gap-2">
                <Link to="/about" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">About</Link>
                <Link to="/blog" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Blog</Link>
                <Link to="/resources" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Resources</Link>
              </div>
            </div>
            <div className="space-y-4">
              <h5 className="text-[10px] font-mono uppercase tracking-widest text-white">Trust</h5>
              <div className="flex flex-col gap-2">
                <Link to="/docs" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Documentation</Link>
                <Link to="/terms" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Terms</Link>
                <Link to="/privacy" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Privacy</Link>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-16 pt-8 border-t border-zinc-900 flex justify-between items-center text-[8px] font-mono uppercase tracking-widest text-zinc-700">
          <span>© 2026 Yacht Labs Inc. / All Rights Reserved.</span>
          <span>Latency: 24ms / Status: Nominal</span>
        </div>
      </footer>
    </div>
  );
};

export default About;
